import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Observable, take } from 'rxjs';
import { selectAuth, State } from 'src/app/store/auth/auth.reducers';
import { environment } from 'src/environments/environment';
import { Auth } from '../models/auth';
import { captureReturnUrl } from '../utils/return-url';
import { OfflineModeState } from 'src/app/core/offline/offline-mode.state';
import { isOfflineAllowedFor, isOfflineAllowedUrl, offlineHomeFor } from 'src/app/core/offline/offline-routes';

@Injectable({
  providedIn: 'root'
})
export class CheckAuthService {
  private auth!: Auth;

  constructor(private store: Store<State>, private _router: Router, private offlineMode: OfflineModeState) {
    this.store.pipe(select(selectAuth)).subscribe(auth => this.auth = auth);
  }

  canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> | Promise<boolean> | boolean {
    // canLoad has no RouterStateSnapshot, so the attempted URL is unavailable here
    // and the post-login return URL cannot be captured on this path.
    return this.check(route);
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean  {
    return this.check(route, state.url);
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.check(childRoute, state.url);
  }

  private check(route: Route | ActivatedRouteSnapshot, attemptedUrl?: string) {
    return new Observable<boolean>((observer) => {

      if (this.auth) {
        observer.next(this.checkAuth(this.auth, route.data && route.data['roles'] ? route.data['roles'] : null, attemptedUrl));
        observer.complete();
      } else {
        this.store.pipe(select(selectAuth), take(1)).subscribe(auth => {
          observer.next(this.checkAuth(auth, route.data && route.data['roles'] ? route.data['roles'] : null, attemptedUrl));
          observer.complete();
        });
      }
    });
  }

  getUserId() {
    return this.auth.userId;
  }


  private checkAuth(auth: Auth, roles?: string[] | null, attemptedUrl?: string): boolean {
    this.auth = auth;
    let valid = false;
    if (!auth.isAuthenticated && this.offlineMode.active) {
      return this.checkOffline(roles, attemptedUrl);
    }
    if (!auth.isAuthenticated) {

      // MR !75 review (#2) — LEFT AS-IS deliberately. The capture is intentionally UNSTAMPED (no owner
      // uid): this branch only runs once `auth` is already EMPTY_AUTH, so there is no expiring-user id to
      // stamp with. The interceptor's endSession() owner-stamps the session-expiry deep-link BEFORE
      // clearing auth (return-url.ts); the only `/app` URL this guard can capture while unauthenticated is
      // a genuinely anonymous deep-link, which is correctly restorable for whoever logs in next. So there
      // is no cross-user leak to close here — do NOT add a stamp (there is nothing to stamp with).
      captureReturnUrl(attemptedUrl);

      this._router.navigateByUrl('/auth');

      valid = false;
    } else if (roles) {

      valid = roles.findIndex(r => this.isEnabled(r)) !== -1;
      if (!valid) {
        this._router.navigateByUrl(environment.homePage);
      }
    } else {
      valid = true;
    }

    return valid;
  }
  /**
   * OFFLINE MODE (no session, an unverified remembered identity -- see OfflineModeState): only the
   * catalogue's route trees are admitted, and only for an identity whose remembered roles satisfy the
   * route's `roles` the way a session's would AND whose confined roles' scopes allow the URL (the
   * ROLE_SCOPES confinement RoleScopeGuard applies online; `isOfflineAllowedFor`). Anything else goes
   * to the offline home (`offlineHomeFor`:
   * Intelligence > Scan, or the printings browse for roles that do not open Scan) --
   * never to /auth, which cannot log anyone in without a server. No return URL is captured: there is
   * no login on the way back to restore it from.
   */
  private checkOffline(roles: string[] | null | undefined, attemptedUrl?: string): boolean {
    const identity = this.offlineMode.identity;
    const allowed =
      !!identity &&
      isOfflineAllowedFor(identity.roles, attemptedUrl) &&
      (!roles || roles.some((r) => identity.roles.includes(r)));
    if (!allowed) {
      if (isOfflineAllowedUrl(attemptedUrl)) {
        // A catalogue route the remembered roles do not open: nothing offline can serve this
        // identity, and redirecting to the catalogue would only be refused again, in a loop. The
        // entry check (`canUseOfflineCatalog`) makes this unreachable today; it stays fail-closed.
        this.offlineMode.exit();
        this._router.navigateByUrl('/auth');
      } else {
        this._router.navigateByUrl(offlineHomeFor(identity?.roles));
      }
    }
    return allowed;
  }

  isEnabled(...roles: string[]) {

      if (this.auth && this.auth.isAuthenticated) {
        if ( roles && roles.length > 0) {
          return roles.every( needRole => this.auth.roles.findIndex( role => role == needRole) !== -1);
        }
        return false;
      }

      return false;
  }
}
